import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import careerReportService from '../services/careerReport.service';

const ReportsContext = createContext();

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports must be used within a ReportsProvider');
  }
  return context;
};

const STORAGE_KEY = 'career_clarity_reports';

const PROFILE_TYPE_LABELS = {
  student: 'Student',
  fresher: 'Fresher',
  professional: 'Professional',
};

const generateReportName = (profileType, existingReports) => {
  const label = PROFILE_TYPE_LABELS[profileType] || 'Report';
  const sameTypeCount = existingReports.filter(
    (r) => r.profileType === profileType
  ).length;
  return `${label} Report ${sameTypeCount + 1}`;
};

export const createEmptyReportData = () => ({
  // Common fields
  careerStage: '',
  selectedCourse: null,
  selectedSpecialisation: null,
  selectedSubjects: [],
  coursePreference: '',
  abroadEducation: false,
  medicalIssue: false,
  selectedState: null,
  selectedUniversities: [],
  examType: '',
  customExam: '',
  selectedExams: [],
  selectedAptitudeTopics: [],
  opportunityType: '',
  selectedPersonalityTraits: [],
  selectedCareerDomains: [],
  // Fresher-specific fields
  pursuingCourse: '',
  courseDuration: '',
  campusName: '',
  fresherAddress: '',
  fresherState: '',
  fresherDistrict: '',
  cgpaRange: '',
  selectedIndustryOutlook: [],
  jobSector: '',
  selectedJobs: [],
});

const buildNewReport = (profileType = '', existingReports = []) => ({
  id: crypto.randomUUID(),
  userId: null, // populated on DB integration
  reportName: existingReports.length === 0
    ? 'My First Report'
    : generateReportName(profileType, existingReports),
  profileType: profileType || '',
  isCustomName: false, // true only after user manually renames
  isProfileTypeLocked: false, // true after user fills any data beyond profile type
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  status: 'in_progress', // 'in_progress' | 'completed'
  completedSteps: 0,
  totalSteps: 8,
  data: createEmptyReportData(),
});

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const loadActiveIdFromStorage = (reports) => {
  try {
    const id = localStorage.getItem(`${STORAGE_KEY}_active_id`);
    if (id && reports.find((r) => r.id === id)) return id;
    return reports.length > 0 ? reports[reports.length - 1].id : null;
  } catch {
    return reports.length > 0 ? reports[reports.length - 1].id : null;
  }
};

export const ReportsProvider = ({ children }) => {
  const [reports, setReports] = useState([]);
  const [activeReportId, setActiveReportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModalCallback, setShowUpgradeModalCallback] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Load reports from database on mount
  useEffect(() => {
    if (hasLoadedOnce) return;

    const loadReports = async () => {
      try {
        setLoading(true);
        const data = await careerReportService.getAllReports();
        // console.log('Loaded reports from database:', data);
        setReports(data || []);
        
        // Set active report
        const activeReport = data?.find(r => r.isActive);
        if (activeReport) {
          // console.log('Setting active report:', activeReport);
          setActiveReportId(activeReport.id);
        } else if (data && data.length > 0) {
          // console.log('No active report found, using last report');
          setActiveReportId(data[data.length - 1].id);
        }
        setHasLoadedOnce(true);
      } catch (err) {
        console.error('Failed to load reports:', err);
        
        const errorStatus = err.response?.status;
        if (errorStatus >= 500) {
          setError('Server error. Please try again later.');
        } else if (errorStatus === 429) {
          setError('Too many requests. Please wait a moment.');
        } else if (!err.response) {
          setError('Cannot connect to server. Please check your connection.');
        } else {
          setError(err.message || 'Failed to load reports');
        }
        
        setHasLoadedOnce(true);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [hasLoadedOnce]);

  const activeReport = reports.find((r) => r.id === activeReportId) ?? null;

  // Ensure at least one report always exists
  const ensureFirstReport = useCallback(async () => {
    if (reports.length === 0) {
      try {
        const newReport = await careerReportService.createReport({ reportName: 'My First Report' });
        setReports([newReport]);
        setActiveReportId(newReport.id);
        return newReport;
      } catch (err) {
        console.error('Failed to create first report:', err);
        setError(err.message);
        return null;
      }
    }
    return null;
  }, [reports]);

  // Create a brand-new blank report and make it active
  const createNewReport = useCallback(
    async (profileType = '') => {
      try {
        const newReport = await careerReportService.createReport({ profileType });
        setReports((prev) => [...prev, newReport]);
        setActiveReportId(newReport.id);
        return newReport;
      } catch (err) {
        console.error('Failed to create report:', err);
        setError(err.message);
        
        // Check if it's a plan limit error (403 status)
        if (err.response?.status === 403 || err.message?.includes('plan limit')) {
          toast.error(err.message || 'You have reached your plan limit. Please upgrade to create more reports.', {
            duration: 6000,
            icon: '🔒',
          });
        } else {
          toast.error('Failed to create report. Please try again.');
        }
        
        throw err;
      }
    },
    [reports]
  );

  // Switch the active report by ID
  const switchReport = useCallback(async (reportId) => {
    try {
      // Set active report on backend
      await careerReportService.setActiveReport(reportId);
      
      // Fetch the complete report data from database
      const fullReport = await careerReportService.getReportById(reportId);
      
      // Update active report ID
      setActiveReportId(reportId);
      
      // Update local state with full report data and mark as active
      setReports((prev) =>
        prev.map((r) => {
          if (r.id === reportId) {
            return { ...fullReport, isActive: true };
          }
          return { ...r, isActive: false };
        })
      );
    } catch (err) {
      console.error('Failed to switch report:', err);
      setError(err.message);
    }
  }, []);

  // Update the data payload of the currently active report
  const updateActiveReportData = useCallback(
    async (newData, completionInfo = null) => {
      if (!activeReportId) return;

      try {
        // Call API to update report data
        const updatedReport = await careerReportService.updateReportData(
          activeReportId,
          newData,
          completionInfo
        );

        // Update local state with response from server
        setReports((prev) =>
          prev.map((report) =>
            report.id === activeReportId ? updatedReport : report
          )
        );
      } catch (err) {
        console.error('Failed to update report data:', err);
        setError(err.message);
      }
    },
    [activeReportId]
  );

  // Rename a report
  const renameReport = useCallback(async (reportId, newName) => {
    try {
      const updatedReport = await careerReportService.renameReport(reportId, newName);
      setReports((prev) =>
        prev.map((r) => (r.id === reportId ? updatedReport : r))
      );
    } catch (err) {
      console.error('Failed to rename report:', err);
      setError(err.message);
    }
  }, []);

  // Delete a report and switch to the latest remaining one
  const deleteReport = useCallback(
    async (reportId) => {
      try {
        await careerReportService.deleteReport(reportId);
        
        setReports((prev) => {
          const updated = prev.filter((r) => r.id !== reportId);
          if (activeReportId === reportId) {
            const nextId = updated.length > 0 ? updated[updated.length - 1].id : null;
            setActiveReportId(nextId);
          }
          return updated;
        });
      } catch (err) {
        console.error('Failed to delete report:', err);
        setError(err.message);
      }
    },
    [activeReportId]
  );

  const value = {
    reports,
    activeReportId,
    activeReport,
    loading,
    error,
    createNewReport,
    switchReport,
    updateActiveReportData,
    renameReport,
    deleteReport,
    ensureFirstReport,
    createEmptyReportData,
    setShowUpgradeModalCallback,
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};
